import { Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Footer } from "../../common/components/Footer/Footer";
import { Header } from "../../common/components/Header/Header";
import { LinearProgressIndicator } from "../../common/components/LinearProgressIndicator/LinearProgressIndicator";
import { LoadingIndicator } from "../../common/components/LoadingIndicator/LoadingIndicator";
import { View } from "../../common/components/View/View";
import { footerSelectorID } from "../../common/Constants";
import { routes } from "../../common/routes/routes";
import { AnswerOption } from "../../common/types/QuestionnaireEntity";
import { openMessageBar } from "../../features/messageBar/messageBarSlice";
import {
  answerQuestion,
  fetchUserResponsesAsync,
  getNextQuestion,
  getPreviousQuestion,
  resetQuestionnaire,
  selectActiveQuestionnaireCompletion,
  selectCurrentQuestion,
  selectCurrentQuestionAnswer,
  selectCurrentQuestionRequired,
  selectQuestionnaireById,
  selectResponsesGiven,
  selectStateStatus,
  updateHistoricalAnswers,
  updateUserResponseAsync,
} from "../../features/questionnaire/questionnaireSlice";
import { selectPatientId } from "../../features/user/userSlice";
import { Purpose } from "../Purpose/Purpose";
import { QuestionnaireContainer } from "./QuestionnaireContainer";

export const Questionnaire = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { id } = useParams();

  const questionnaire = useAppSelector((state) => {
    return selectQuestionnaireById(state, id ?? "");
  });
  const progress = useAppSelector(selectActiveQuestionnaireCompletion);
  const answeredQuestionnaires = useAppSelector(selectResponsesGiven);
  const questionToDisplay = useAppSelector(selectCurrentQuestion);
  const currentQuestionAnswer = useAppSelector(selectCurrentQuestionAnswer);
  const isCurrentQuestionRequired = useAppSelector(
    selectCurrentQuestionRequired
  );
  const [hasAnswer, setHasAnswer] = useState(false);
  const [customHasAnswer, setCustomHasAnswer] = useState(true);
  const dispatch = useAppDispatch();
  const stateStatus = useAppSelector(selectStateStatus);
  const patientId = useAppSelector(selectPatientId);

  useEffect(() => {
    setHasAnswer(
      currentQuestionAnswer?.value.detailedResponse !== "" ||
        (currentQuestionAnswer?.value.multiSelect &&
          currentQuestionAnswer.value.multiSelect.length > 0) ||
        currentQuestionAnswer?.value.response !== undefined
    );
  }, [currentQuestionAnswer, questionToDisplay]);

  useEffect(() => {
    // Get options selected
    const optionsSelected = questionToDisplay?.answerOption.filter((elem) => {
      const hasResponse =
        elem.value.string === currentQuestionAnswer?.value.response;
      const hasMultiResponse =
        currentQuestionAnswer?.value.multiSelect?.includes(elem.value.string);
      return hasResponse || hasMultiResponse;
    });
    // If any answer is custom and has no response, question isn't answered;
    // get is custom
    const customOptions: AnswerOption[] =
      optionsSelected?.filter((option): boolean => {
        let isHint: boolean;
        if (option.value.Reference?.display !== undefined) {
          isHint = option.value.Reference?.display.includes("e.g.") || false;
        } else {
          isHint = true;
        }
        const containsOther: boolean =
          option.value.string.toLowerCase().includes("other") || false;
        return containsOther || !isHint;
      }) ?? [];
    if (customOptions.length > 0) {
      // Has custom option and detailed response, so true
      if (currentQuestionAnswer?.value.detailedResponse) {
        setCustomHasAnswer(true);
      } else {
        setCustomHasAnswer(false);
      }
      // Has custom option and no detailed response so false
    } else {
      // No custom response, so it passes
      setCustomHasAnswer(true);
    }
  }, [currentQuestionAnswer, questionToDisplay]);

  const submitQuestionnaire = async () => {
    const questionnaireName = questionnaire?.name;
    if (questionnaireName) {
      await dispatch(updateUserResponseAsync());
      dispatch(fetchUserResponsesAsync());
      dispatch(
        openMessageBar({
          message: `${questionnaireName} ${formatMessage({
            id: "messageBar.messages.success.questionnaireCompleted",
          })}`,
          status: "success",
        })
      );
      navigate(routes.DASHBOARD);
    }
  };

  const updateUserResponse = async (value: any) => {
    const newAnswer = {
      questionId: questionToDisplay?.id as string,
      questionType: questionToDisplay?.type as string,
      patientId: patientId ?? "",
      value: value,
    };
    const isSamePatient: boolean =
      newAnswer.patientId === currentQuestionAnswer?.patientId;
    const isSameQuestion: boolean =
      newAnswer.questionId === currentQuestionAnswer?.questionId;
    const isSameResponse: boolean =
      newAnswer.value.response === currentQuestionAnswer?.value.response;
    const isSameMultiSelect: boolean =
      newAnswer.value.detailedResponse ===
      currentQuestionAnswer?.value.detailedResponse;
    const isSameDetailText: boolean =
      newAnswer.value.multiSelect === currentQuestionAnswer?.value.multiSelect;
    const doesMatch =
      isSamePatient &&
      isSameQuestion &&
      isSameResponse &&
      isSameMultiSelect &&
      isSameDetailText;
    if (!doesMatch) {
      await dispatch(answerQuestion(newAnswer));
      await dispatch(updateUserResponseAsync());
    }
  };

  const labelNextButton =
    progress === 100
      ? formatMessage({ id: "footer.finishButton" })
      : formatMessage({ id: "footer.nextButton" });

  if (stateStatus === "idle" && questionnaire) {
    return (
      <View
        isQuestionnaire
        route={questionToDisplay?.id}
        header={
          <div
            style={{
              backgroundColor: "rgb(242, 241, 245)",
              zIndex: 1199,
              width: "100vw",
              position: "fixed",
              left: 0,
              top: 0,
            }}
          >
            <Header
              sx={{
                padding: "1.75rem 1.25rem 10px",
              }}
              isCloseVisible
              onClose={async () => {
                dispatch(resetQuestionnaire());
                navigate(routes.DASHBOARD);
                dispatch(fetchUserResponsesAsync());
              }}
            />
            {questionToDisplay && <LinearProgressIndicator value={progress} />}
          </div>
        }
      >
        <>
          {!questionToDisplay && (
            <Purpose
              title={questionnaire.name}
              purpose={questionnaire.purpose}
              onContinue={() => {
                dispatch(getNextQuestion());
                if (
                  questionnaire &&
                  answeredQuestionnaires.includes(questionnaire.id)
                ) {
                  dispatch(updateUserResponseAsync());
                }
              }}
            />
          )}

          {questionToDisplay && (
            <div>
              <Typography
                margin="55px 0"
                component="h1"
                fontSize="1.5em"
                fontWeight="700"
                textAlign="center"
              >
                {questionnaire.name}
              </Typography>
              <QuestionnaireContainer
                question={questionToDisplay}
                existingAnswer={currentQuestionAnswer}
                updateAnswer={updateUserResponse}
              />
              <Footer
                id={footerSelectorID}
                isSkipVisible={!isCurrentQuestionRequired && customHasAnswer}
                isPreviousDisabled={false}
                labelNextButton={labelNextButton}
                isNextDisabled={
                  (isCurrentQuestionRequired && !hasAnswer) || !customHasAnswer
                }
                onNextClick={() => {
                  if (progress === 100) {
                    submitQuestionnaire();
                  } else {
                    dispatch(updateHistoricalAnswers());
                    dispatch(getNextQuestion());
                  }
                }}
                onPreviousClick={() => {
                  dispatch(getPreviousQuestion());
                }}
                onSkipClick={() => {
                  dispatch(getNextQuestion());
                }}
              />
            </div>
          )}
        </>
      </View>
    );
  } else if (stateStatus === "loading") {
    return <LoadingIndicator isLoading />;
  }
  return <Navigate to={"/"} />;
};
